import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Loader2, Upload, Check, FileText, Film, ImageIcon } from "lucide-react";
import { adminApi, mediaRaw } from "./adminApi";
import { toast } from "sonner";

export function StatusBadge({ status }) {
  const map = {
    published: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
    draft: "bg-amber-500/15 text-amber-300 border-amber-500/30",
    archived: "bg-slate-500/15 text-slate-300 border-slate-500/30",
  };
  return <Badge className={`border ${map[status] || map.draft} capitalize`} data-testid={`status-${status}`}>{status}</Badge>;
}

export function Field({ label, children, hint }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-slate-300">{label}</Label>
      {children}
      {hint && <p className="text-xs text-slate-500">{hint}</p>}
    </div>
  );
}

// Textarea-backed list editor (one item per line)
export function LinesEditor({ label, value, onChange, placeholder, testId }) {
  return (
    <Field label={label} hint="One item per line">
      <textarea
        data-testid={testId}
        value={(value || []).join("\n")}
        onChange={(e) => onChange(e.target.value.split("\n").map((s) => s.trim()).filter(Boolean))}
        placeholder={placeholder}
        rows={4}
        className="w-full rounded-lg border border-slate-700 bg-[#0b1730] px-3 py-2 text-sm text-white outline-none focus:border-cyan-500"
      />
    </Field>
  );
}

const KIND_ICON = { image: ImageIcon, video: Film, pdf: FileText };

export function MediaPicker({ open, onClose, kind, onSelect }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [pct, setPct] = useState(0);

  const load = async () => {
    setLoading(true);
    try { setItems(await adminApi.listMedia(kind)); } finally { setLoading(false); }
  };
  useEffect(() => { if (open) load(); /* eslint-disable-next-line */ }, [open, kind]);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true); setPct(0);
    try {
      const m = await adminApi.uploadMedia(file, file.name, "", setPct);
      toast.success("Uploaded");
      await load();
      onSelect(m);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  };

  const Icon = KIND_ICON[kind] || ImageIcon;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl bg-[#0b1730] border-cyan-500/20 text-white" data-testid="media-picker">
        <DialogHeader><DialogTitle className="font-head">Select {kind}</DialogTitle></DialogHeader>
        <label className="flex cursor-pointer items-center justify-center gap-2 rounded-lg border border-dashed border-cyan-500/40 py-4 text-cyan-200 hover:bg-cyan-500/5" data-testid="media-picker-upload">
          {uploading ? <><Loader2 className="h-4 w-4 animate-spin" /> Uploading {pct}%</> : <><Upload className="h-4 w-4" /> Upload new {kind}</>}
          <input type="file" className="hidden" onChange={upload} disabled={uploading}
            accept={kind === "image" ? "image/*" : kind === "video" ? "video/*" : "application/pdf"} />
        </label>
        <div className="grid max-h-[50vh] grid-cols-2 gap-3 overflow-y-auto sm:grid-cols-3">
          {loading ? <Loader2 className="h-6 w-6 animate-spin text-cyan-400" /> :
            items.length === 0 ? <p className="col-span-full py-6 text-center text-sm text-slate-500">No {kind} files yet.</p> :
            items.map((m) => (
              <button key={m.id} onClick={() => onSelect(m)} data-testid={`media-item-${m.id}`}
                className="group overflow-hidden rounded-lg border border-slate-700 bg-[#050b18] text-left hover:border-cyan-500">
                {m.kind === "image" ? (
                  <img src={mediaRaw(m.id)} alt={m.title} className="h-24 w-full object-cover" />
                ) : (
                  <div className="flex h-24 items-center justify-center bg-[#122244]"><Icon className="h-8 w-8 text-cyan-300" /></div>
                )}
                <div className="p-2"><p className="truncate text-xs text-slate-200">{m.title}</p></div>
              </button>
            ))}
        </div>
        <DialogFooter><Button variant="ghost" onClick={onClose}>Close</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
